import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, type MapRef, Marker, Source } from 'react-map-gl';

import { StarFilledIcon } from '@radix-ui/react-icons';
import {
  BusIcon,
  ChevronDown,
  CookingPotIcon,
  HeadphonesIcon,
  LeafIcon,
  ShoppingBagIcon,
} from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MapBox } from '@/components/ui/map';
import { Switch } from '@/components/ui/switch';
import { env } from '@/constants/env';
import type { Estate } from '@/features/estates-property/api/estate';
import { cn } from '@/lib/utils';
import { createCircleGeoJSON } from '@/utils/geoJSON';

import type { EstateAnalysis } from '../api/analytics';
import { useLocationScores } from '../api/location-scores';
import type { PointsOfInterest as POI } from '../api/points-of-interest';
import { usePointsOfInterest } from '../api/points-of-interest';
import EducationIcon from './icons/education-icon';
import HealthIcon from './icons/health-icon';

const MAP_BOX_TOKEN = env.MAP_BOX_API_TOKEN;

const ICON_MAPPING: Record<string, JSX.Element> = {
  shopping: <ShoppingBagIcon size={18} color="#C7D422" />,
  leisure: <LeafIcon size={18} color="#BE0707" />,
  health: <HealthIcon />,
  education: <EducationIcon />,
  noise: <HeadphonesIcon size={18} color="#E8A014" />,
  catering: <CookingPotIcon size={18} color="#07BEA8" />,
  transport: <BusIcon size={18} color="#E8A014" />,
};

const COLOR_MAPPING: Record<string, string> = {
  shopping: '#DCEB29',
  leisure: '#BE0707',
  health: '#3BB03D',
  education: '#0784BE',
  noise: '#E8A014',
  catering: '#07BEA8',
  transport: '#E8A014',
};

const RADIUS_OPTIONS = [
  { label: '100m', value: 100, minZoom: 17 },
  { label: '300m', value: 300, minZoom: 16 },
  { label: '500m', value: 500, minZoom: 15 },
  { label: '1km', value: 1000, minZoom: 14 },
  { label: '4km', value: 4000, minZoom: 12 },
];

export const PointsOfInterest = ({
  data: estate,
  estateAnalysis,
}: {
  data: Estate;
  estateAnalysis: EstateAnalysis;
}) => {
  const { t } = useTranslation();
  const [radius, setRadius] = useState(RADIUS_OPTIONS[3]);
  const [openedItems, setOpenedItems] = useState<string[]>(Object.keys(COLOR_MAPPING));

  const { data: pointsOfInterest } = usePointsOfInterest(estateAnalysis, radius.value);
  const { data: locationScores } = useLocationScores(estateAnalysis);

  const mapRef = useRef<MapRef>(null);

  const circleGeoJson = useMemo(() => {
    return createCircleGeoJSON(
      [Number(estate?.address?.long), Number(estate?.address?.lat)],
      radius.value / 1000
    );
  }, [estate, radius]);

  const mapAnchors = useMemo(() => {
    const anchors = [`pin-s-x+000000(${estate?.address?.long},${estate?.address?.lat})`];
    if (pointsOfInterest) {
      Object.entries(pointsOfInterest).map(([key, data]) => {
        if (openedItems.includes(key)) {
          data.forEach((poi, idx) => {
            if (idx < 4) {
              anchors.push(
                `pin-s-${String.fromCharCode(65 + idx).toLowerCase()}+${COLOR_MAPPING[key].replace('#', '')}(${poi.location.coordinates.longitude},${poi.location.coordinates.latitude})`
              );
            }
          });
        }
      });
    }
    return anchors;
  }, [estate, pointsOfInterest, openedItems]);

  const getPOIName = useCallback((poi: POI) => {
    if (poi.category === 'transport') {
      return `${poi.subcategory}${poi.name ? `, ${poi.name}` : ''}`;
    } else {
      return poi.name || poi.subcategory;
    }
  }, []);

  const handleChangeRadius = useCallback(
    (r: typeof radius) => {
      setRadius(r);
      if (mapRef.current) {
        mapRef.current.jumpTo({
          zoom: r.minZoom,
          center: estate?.address
            ? [Number(estate.address.long), Number(estate.address.lat)]
            : undefined,
        });
      }
    },
    [estate]
  );

  return (
    <div>
      <h2 className="mb-6 text-md font-bold text-primary-dark print:hidden">
        {t('pointsOfInterest')}
      </h2>
      <div className="gap-6 space-y-6 sm:grid sm:grid-cols-[1fr_320px] sm:space-y-0 print:flex print:flex-col print:gap-2">
        {/* Map */}
        <Card className="print:hidden">
          <div className="flex p-6 pb-4">
            <div className="flex-1">
              <h3 className="text-md font-bold text-primary-dark">{t('map')} </h3>
              <p className="text-sm text-text-secondary">{t('lastWeek')}</p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  <span>
                    {t('radius')} {radius.label}
                  </span>
                  <ChevronDown aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {RADIUS_OPTIONS.map((r) => (
                  <DropdownMenuItem key={r.value} onSelect={() => handleChangeRadius(r)}>
                    <span>{r.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="h-[400px] md:h-[600px] lg:h-[752px]">
            <MapBox ref={mapRef} latitude={estate?.address?.lat} longitude={estate?.address?.long}>
              {pointsOfInterest
                ? Object.entries(pointsOfInterest).map(([key, data]) =>
                    openedItems.includes(key)
                      ? data.map((poi, idx) => (
                          <Marker
                            key={`${key}_${poi.name}_${poi.distance}`}
                            latitude={poi.location.coordinates.latitude}
                            longitude={poi.location.coordinates.longitude}
                            anchor="bottom"
                          >
                            <div className="relative flex flex-col items-center">
                              <div
                                className="flex h-6 w-6 items-center justify-center rounded text-xs font-semibold text-white shadow-md"
                                style={{ backgroundColor: COLOR_MAPPING[key] }}
                              >
                                {String.fromCharCode(65 + idx)}
                              </div>
                              <div
                                className="h-0 w-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent"
                                style={{ borderTopColor: COLOR_MAPPING[key] }}
                              />
                            </div>
                          </Marker>
                        ))
                      : null
                  )
                : null}
              <Source id="circle" type="geojson" data={circleGeoJson}>
                <Layer
                  id="dotted-circle"
                  type="line"
                  source="circle"
                  paint={{
                    'line-color': '#000000',
                    'line-width': 2,
                    'line-dasharray': [2, 2],
                  }}
                />
              </Source>
            </MapBox>
          </div>
        </Card>
        {/* Map in print mode */}
        <p className="hidden text-end text-sm print:block">
          {t('radius')} {radius.label}
        </p>
        <img
          src={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${mapAnchors.join(',')}/${estate?.address?.long},${estate?.address?.lat},14,0,0/1000x560?access_token=${MAP_BOX_TOKEN}`}
          alt="Static map"
          className="hidden rounded-md print:block"
        />
        {/* POIs */}
        <Card className="print:p-0 print:shadow-none">
          <div className="border-b p-6 pb-4 print:hidden">
            <h3 className="text-md font-bold text-primary-dark">POIs</h3>
          </div>
          <div className="h-[400px] space-y-6 overflow-auto py-4 md:h-[600px] lg:h-[752px] print:h-fit">
            {Object.entries(pointsOfInterest).length === 0 ? (
              <p className="text-center text-sm text-text-secondary">{t('noPOIs')}</p>
            ) : (
              <Accordion
                type="multiple"
                value={openedItems}
                className="space-y-6 px-6 print:grid print:grid-cols-2 print:gap-4 print:space-y-0 print:px-0"
                onValueChange={(values) => setOpenedItems(values)}
                defaultChecked
              >
                {Object.entries(pointsOfInterest).map(([key, data]) => (
                  <AccordionItem key={key} value={key} className="border-b pb-4">
                    <AccordionTrigger
                      className="p-0 text-[20px] font-bold print:gap-0"
                      arrowIcon={
                        <Switch checked={openedItems.includes(key)} className="print:hidden" />
                      }
                    >
                      <div key={key} className="flex flex-1 items-center">
                        <div className="flex flex-1 items-center gap-4">
                          <div
                            className="flex min-h-6 min-w-6 items-center justify-center rounded print:min-h-8 print:min-w-8"
                            style={{ backgroundColor: `${COLOR_MAPPING[key]}25` }}
                          >
                            {ICON_MAPPING[key]}
                          </div>
                          <p className="text-base font-bold capitalize text-primary print:text-[16px]">
                            {t(key, {
                              defaultValue: key,
                            })}
                          </p>
                        </div>
                        {locationScores?.internalScores?.[key] ? (
                          <>
                            <StarFilledIcon className="text-primary print:h-5 print:w-5" />
                            <p className="text-base font-normal capitalize text-primary print:text-[16px]">
                              {locationScores?.internalScores?.[key]}
                            </p>
                          </>
                        ) : null}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-2 pt-2 print:space-y-1 print:pt-1">
                      {data.map((poi, idx) => (
                        <div
                          key={`${key}_${poi.subcategory}_${poi.name}_${poi.distance}`}
                          className={cn(
                            'flex items-center justify-between gap-4 print:pl-1',
                            idx >= 4 && 'print:hidden'
                          )}
                        >
                          <div
                            className="flex min-h-6 min-w-6 items-center justify-center rounded text-sm text-white"
                            style={{ backgroundColor: COLOR_MAPPING[key] }}
                          >
                            {String.fromCharCode(65 + idx)}
                          </div>
                          <p className="flex-1 text-sm font-normal capitalize leading-tight">
                            {getPOIName(poi)}
                          </p>
                          <p className="text-sm font-normal italic text-primary-dark">
                            {poi.distance}m
                          </p>
                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
